import React from 'react';
import { Checkbox, FormControlLabel, TextField, Typography } from '@mui/material';
import { CodeAgentRemoteSettings, CodeAgentRiskLevel } from '../../types/code-agent';
import { codeAgentFieldSx } from './codeAgentFieldStyles';

// Mirrors aibe DEFAULT_CODE_AGENT_REMOTE; only used until the server returns its own config.
export const DEFAULT_REMOTE: CodeAgentRemoteSettings = {
  enabled: true,
  maxLinksPerSession: 1,
  pairingTtlSeconds: 600,
  remoteApprovableRisks: ['read', 'write', 'execute', 'network'],
  offlineThresholdSeconds: 75,
  offlineAlertCooldownSeconds: 300,
  queueReminderSeconds: 1800,
  rateLimitPerMinute: 30,
  summaryMaxChars: 1500,
  offlineInboxMax: 20,
};

type NumericKey =
  | 'maxLinksPerSession'
  | 'pairingTtlSeconds'
  | 'offlineThresholdSeconds'
  | 'offlineAlertCooldownSeconds'
  | 'queueReminderSeconds'
  | 'rateLimitPerMinute'
  | 'summaryMaxChars'
  | 'offlineInboxMax';

const NUMERIC_FIELDS: { key: NumericKey; label: string }[] = [
  { key: 'maxLinksPerSession', label: 'Chats allowed per session' },
  { key: 'pairingTtlSeconds', label: 'Pairing code lifetime (s)' },
  { key: 'offlineThresholdSeconds', label: 'Laptop silent = offline after (s)' },
  { key: 'offlineAlertCooldownSeconds', label: 'Offline alert cooldown (s)' },
  { key: 'queueReminderSeconds', label: 'Reminder for queued phone commands (s)' },
  { key: 'rateLimitPerMinute', label: 'Phone commands per minute' },
  { key: 'summaryMaxChars', label: 'Summary length sent to phone (chars)' },
  { key: 'offlineInboxMax', label: 'Max commands queued while offline' },
];

// The server always strips these two, so they are not offered here.
const APPROVABLE_RISKS: CodeAgentRiskLevel[] = ['read', 'write', 'execute', 'network'];

interface Props {
  remote: CodeAgentRemoteSettings;
  onChange: (value: CodeAgentRemoteSettings) => void;
}

export function CodeAgentRemoteSection({ remote, onChange }: Props) {
  const toggleRisk = (risk: CodeAgentRiskLevel) =>
    onChange({
      ...remote,
      remoteApprovableRisks: remote.remoteApprovableRisks.includes(risk)
        ? remote.remoteApprovableRisks.filter((item) => item !== risk)
        : [...remote.remoteApprovableRisks, risk],
    });

  return (
    <div>
      <Typography className="mb-1 font-semibold text-gray-900 dark:text-slate-100">
        Remote control (phone via Telegram)
      </Typography>
      <Typography variant="body2" className="mb-3 text-gray-500 dark:text-slate-400">
        Lets a user send tasks and approve tool calls from Telegram while the laptop CLI stays online.
        Also requires the code_agent_remote capability on the user's package. Destructive and
        credential-sensitive actions, and dangerous shell commands, are always approved on the laptop only.
      </Typography>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {NUMERIC_FIELDS.map(({ key, label }) => (
          <TextField
            key={key}
            size="small"
            label={label}
            type="number"
            value={remote[key]}
            onChange={(e) =>
              onChange({ ...remote, [key]: parseInt(e.target.value, 10) || DEFAULT_REMOTE[key] })
            }
            sx={codeAgentFieldSx}
            inputProps={{ min: 1 }}
          />
        ))}
      </div>
      <div className="mt-2 flex flex-col">
        <FormControlLabel
          control={
            <Checkbox
              checked={remote.enabled}
              onChange={(e) => onChange({ ...remote, enabled: e.target.checked })}
            />
          }
          label="Remote control enabled (kill switch)"
        />
        <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
          Risk levels the phone may approve
        </Typography>
        <div className="flex flex-wrap">
          {APPROVABLE_RISKS.map((risk) => (
            <FormControlLabel
              key={risk}
              control={
                <Checkbox
                  checked={remote.remoteApprovableRisks.includes(risk)}
                  onChange={() => toggleRisk(risk)}
                />
              }
              label={risk}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
